import {
  createFrontendPlugin,
  type FrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  createFromTemplateRouteRef,
  rootRouteRef,
  viewTechDocRouteRef,
} from './routes';
import {
  CatalogFilterBlueprint,
  EntityCardBlueprint,
} from '@backstage/plugin-catalog-react/alpha';

/**
 * TeamSystem replacement for the built-in `entity-card:catalog/about`.
 *
 * `EntityCardBlueprint` already declares `attachTo: { id:
 * 'entity-content:catalog/overview', input: 'cards' }`, so the card only needs
 * to describe itself:
 *
 * - `type: 'info'` places it in the sticky right hand column of
 *   `DefaultEntityContentLayout`, where the built-in About card lives. Cards
 *   without a type (or with `type: 'content'`) go to the wide left column.
 * - `filter` is the same predicate the built-in card uses: `User` and `Group`
 *   have their own profile cards, every other kind gets the About card.
 * - No `variant` is passed: the new layout stacks info cards in a flex column,
 *   so the `gridItem` height hack the deleted `EntityPage.tsx` needed in a MUI
 *   `Grid` would only add a stray 10px bottom margin.
 *
 * The built-in card must be switched off in `app.extensions`, otherwise the
 * overview renders both:
 *
 * ```yaml
 * app:
 *   extensions:
 *     - entity-card:catalog/about: false
 * ```
 *
 * `makeWithOverrides` is only needed to add extension *inputs* — that is how
 * `@backstage/plugin-catalog` lets other plugins contribute icon links to its
 * own About card through `EntityIconLinkBlueprint`. Our card builds its
 * subheader itself (`DefaultAboutCardSubheader`), so plain `make` is enough.
 */
const aboutCard = EntityCardBlueprint.make({
  name: 'about',
  params: {
    type: 'info',
    filter: { $not: { kind: { $in: ['user', 'group'] } } },
    loader: () => import('./components/AboutCard').then(m => <m.AboutCard />),
  },
});

/**
 * Replaces `entity-card:org/group-profile`.
 *
 * Same `type`/`filter` as the card it replaces, so the group overview keeps the
 * profile in the info column.
 */
const groupProfileCard = EntityCardBlueprint.make({
  name: 'group-profile',
  params: {
    type: 'info',
    filter: { kind: 'group' },
    loader: () =>
      import('./components/GroupProfileCard').then(m => <m.GroupProfileCard />),
  },
});

/**
 * Replaces `entity-card:org/members-list`.
 *
 * `type: 'content'` — the members list is a paginated table and used the full
 * row width (`xs={12}`) in the deleted `EntityPage.tsx`, so it belongs in the
 * wide column,
 * not in the narrow info one.
 *
 * `memberDisplayTitle`, `pageSize`, `relationsType` and
 * `showAggregateMembersToggle` keep their component defaults. If they ever need
 * to be tunable per instance, switch to `makeWithOverrides` and expose them
 * through `configSchema` the way `@backstage/plugin-org` does.
 */
const membersListCard = EntityCardBlueprint.make({
  name: 'members-list',
  params: {
    type: 'content',
    filter: { kind: 'group' },
    loader: () =>
      import('./components/MemberListFromEntity').then(
        m => <m.MemberListFromEntity />,
      ),
  },
});

/**
 * Replaces `entity-card:org/ownership`, which the deleted `EntityPage.tsx` had
 * commented
 * out in favour of this card on both the group and the user page.
 *
 * `type: 'info'`: it is a compact `InfoCard` with a list of entity links, so it
 * fits the narrow column — that is also where it sat before (`md={6}`, next to
 * the profile card).
 */
const modulesOwnershipCard = EntityCardBlueprint.make({
  name: 'modules-ownership',
  params: {
    type: 'info',
    filter: { kind: { $in: ['group', 'user'] } },
    loader: () =>
      import('./components/ModuleListCard').then(m => <m.ModuleListCard />),
  },
});

/**
 * The TeamSystem catalog index filters.
 *
 * `CatalogFilterBlueprint` already declares `attachTo: { id: 'page:catalog',
 * input: 'filters' }`, so each filter only has to load its picker. They used to
 * be listed as JSX children of a hand written catalog page; as extensions they
 * are ordered, enabled and disabled from `app.extensions` in `app-config.yaml`
 * together with the built-in `catalog-filter:catalog/*` ones.
 *
 * Every picker still hides itself for the kinds it does not apply to — see
 * `visibleForKinds` in `EntityPickerBase` — so being permanently attached to
 * the page changes nothing for `User`, `Group`, `Area`, … The blueprint has no
 * `filter` param of its own, unlike `EntityCardBlueprint`, so that per-kind
 * decision has to stay in the component.
 *
 * The pickers are imported one module at a time rather than through
 * `./pickers`, so each filter is its own lazy chunk.
 */
const areaFilter = CatalogFilterBlueprint.make({
  name: 'area',
  params: {
    loader: () =>
      import('./pickers/AreaPicker').then(m => <m.EntityAreaPicker />),
  },
});

const functionalDomainFilter = CatalogFilterBlueprint.make({
  name: 'functional-domain',
  params: {
    loader: () =>
      import('./pickers/FunctionalDomainPicker').then(
        m => <m.EntityFunctionalDomainPicker />,
      ),
  },
});

/**
 * Replaces `catalog-filter:catalog/mode`, the stock `EntityOwnerPicker`: this
 * one is labelled "Team" and is backed by the same cursor paginated
 * `EntityPickerBase` as the other TeamSystem pickers.
 */
const ownerFilter = CatalogFilterBlueprint.make({
  name: 'owner',
  params: {
    loader: () =>
      import('./pickers/OwnerPicker').then(m => <m.EntityOwnerPicker />),
  },
});

const engineeringManagerFilter = CatalogFilterBlueprint.make({
  name: 'engineering-manager',
  params: {
    loader: () =>
      import('./pickers/EngineeringManagerPicker').then(
        m => <m.EntityEngineeringManagerPicker />,
      ),
  },
});

const superProductOwnerFilter = CatalogFilterBlueprint.make({
  name: 'super-product-owner',
  params: {
    loader: () =>
      import('./pickers/SuperProductOwnerPicker').then(
        m => <m.EntitySuperProductOwnerPicker />,
      ),
  },
});

/**
 * No catalog index page is declared here.
 *
 * The portal renders the stock `page:catalog`. The app overrides it for one
 * reason only — to hand `catalogColumnsFunc` to the catalog table, because
 * columns are the last part of that page which is still a component prop and
 * not an extension (see `packages/app/src/App.tsx` and
 * `docs/frontend-system-migration.md`).
 *
 * A page extension of our own would not work anyway: it would replace
 * `page:catalog` rather than add a page, and a route ref created with
 * `createRouteRef({ aliasFor: 'catalog.catalogIndex' })` may only be used by an
 * extension that belongs to the `catalog` plugin itself — the app refuses to
 * resolve an alias that points to another plugin. Overriding keeps the
 * catalog's own route ref, so every `catalogIndex` route binding and every
 * `useRouteRef(catalogPlugin.routes.catalogIndex)` call keeps working.
 *
 * The explicit `FrontendPlugin` annotation is not cosmetic: `@backstage/core-plugin-api`
 * still depends on `@backstage/frontend-plugin-api@^0.17.3` while the repo is on
 * `0.18.0`, so the type inferred from the extension list would reference the nested
 * copy of that package and TypeScript refuses to emit it (TS2742).
 */
const catalogTeamSystemComponentsPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'catalog-teamsystem-components',
  extensions: [
    aboutCard,
    groupProfileCard,
    membersListCard,
    modulesOwnershipCard,
    areaFilter,
    functionalDomainFilter,
    ownerFilter,
    engineeringManagerFilter,
    superProductOwnerFilter,
  ],
  routes: {
    root: rootRouteRef,
  },
  externalRoutes: {
    createFromTemplate: createFromTemplateRouteRef,
    viewTechDoc: viewTechDocRouteRef,
  },
});

export default catalogTeamSystemComponentsPlugin;
